<div [attr.padding]="withPadding() ? '' : null" [attr.padding-bottom]="withPadding() ? null : ''">
  @if (infoTitle() || infoDescription() | rtRemoveHTMLTags) {
    <rt-evaluation-form-description
      [title]="infoTitle()"
      [description]="infoDescription()"
      margin-bottom-small
    />
  }

  @for (answer of form.controls.answers.controls; track answer) {
    @switch (answer.controls.questionKind.value) {
      @case (QuestionKind.String) {
        <rt-evaluation-single-line-field
          [control]="answer.controls.content"
          [title]="answer.controls.questionTitle.value ?? ''"
          [hint]="answer.controls.questionHint.value"
          [required]="answer.controls.questionRequired.value"
          [showValidationErrors]="submissionAttempted()"
          [rtEvaluationFieldRef]="answer.controls.content"
          margin-bottom-small
        />
      }
      @case (QuestionKind.Text) {
        <rt-evaluation-text-answer-field
          [control]="answer.controls.content"
          [jsonControl]="answer.controls.contentJson"
          [title]="answer.controls.questionTitle.value ?? ''"
          [hint]="answer.controls.questionHint.value"
          [required]="answer.controls.questionRequired.value"
          [showValidationErrors]="submissionAttempted()"
          [rtEvaluationFieldRef]="
            useProsemirror ? answer.controls.contentJson : answer.controls.content
          "
          margin-bottom-small
        />
      }
      @case (QuestionKind.Scorecard) {
        <rt-evaluation-question-card
          [title]="answer.controls.questionTitle.value ?? ''"
          [hint]="answer.controls.questionHint.value"
          [control]="answer.controls.contents"
          [showValidationErrors]="submissionAttempted()"
          [canComment]="useProsemirror"
          [commentDiscovery]="commentDiscoveryActive()"
          [commentExpanded]="isCommentExpanded(answer)"
          (commentToggle)="toggleComment(answer)"
          [rtEvaluationFieldRef]="answer.controls.contents"
          margin-bottom-small
        >
          <rt-evaluation-scorecard-field
            [contents]="answer.controls.contents"
            [options]="answer.controls.options.getRawValue()"
            [required]="answer.controls.questionRequired.value"
          />
          @if (isCommentExpanded(answer)) {
            <rt-evaluation-comment-field
              questionNote
              [control]="answer.controls.note"
              [jsonControl]="answer.controls.noteJson"
              [hasBox]="false"
              [scrollOnCreate]="wasCommentExpandedByUser(answer)"
              [rtFeatureTooltip]="commentTooltipConfig"
              [rtFeatureTooltipEnabled]="isCommentTooltipOpen(answer)"
              (rtFeatureTooltipDismissed)="onCommentTooltipDismissed()"
              (scrolledOnCreate)="onCommentScrolled(answer)"
              margin-top-small
            />
          }
        </rt-evaluation-question-card>
      }
      @case (QuestionKind.Boolean) {
        <rt-evaluation-question-card
          [title]="answer.controls.questionTitle.value ?? ''"
          [hint]="answer.controls.questionHint.value"
          [required]="answer.controls.questionRequired.value"
          [control]="answer.controls.content"
          [showValidationErrors]="submissionAttempted()"
          [canComment]="useProsemirror"
          [commentDiscovery]="commentDiscoveryActive()"
          [commentExpanded]="isCommentExpanded(answer)"
          (commentToggle)="toggleComment(answer)"
          [rtEvaluationFieldRef]="answer.controls.content"
          margin-bottom-small
        >
          <rt-evaluation-boolean-field [control]="answer.controls.content" />
          @if (isCommentExpanded(answer)) {
            <rt-evaluation-comment-field
              questionNote
              [control]="answer.controls.note"
              [jsonControl]="answer.controls.noteJson"
              [hasBox]="false"
              [scrollOnCreate]="wasCommentExpandedByUser(answer)"
              [rtFeatureTooltip]="commentTooltipConfig"
              [rtFeatureTooltipEnabled]="isCommentTooltipOpen(answer)"
              (rtFeatureTooltipDismissed)="onCommentTooltipDismissed()"
              (scrolledOnCreate)="onCommentScrolled(answer)"
              margin-top-small
            />
          }
        </rt-evaluation-question-card>
      }
      @case (QuestionKind.SingleChoice) {
        <rt-evaluation-question-card
          [title]="answer.controls.questionTitle.value ?? ''"
          [hint]="answer.controls.questionHint.value"
          [required]="answer.controls.questionRequired.value"
          [control]="answer.controls.content"
          [showValidationErrors]="submissionAttempted()"
          [canComment]="useProsemirror"
          [commentDiscovery]="commentDiscoveryActive()"
          [commentExpanded]="isCommentExpanded(answer)"
          (commentToggle)="toggleComment(answer)"
          [rtEvaluationFieldRef]="answer.controls.content"
          margin-bottom-small
        >
          <rt-evaluation-single-choice-field
            [control]="answer.controls.content"
            [options]="toChoiceOptions(answer.controls.options.getRawValue())"
          />
          @if (isCommentExpanded(answer)) {
            <rt-evaluation-comment-field
              questionNote
              [control]="answer.controls.note"
              [jsonControl]="answer.controls.noteJson"
              [hasBox]="false"
              [scrollOnCreate]="wasCommentExpandedByUser(answer)"
              [rtFeatureTooltip]="commentTooltipConfig"
              [rtFeatureTooltipEnabled]="isCommentTooltipOpen(answer)"
              (rtFeatureTooltipDismissed)="onCommentTooltipDismissed()"
              (scrolledOnCreate)="onCommentScrolled(answer)"
              margin-top-small
            />
          }
        </rt-evaluation-question-card>
      }
      @case (QuestionKind.MultipleChoice) {
        <rt-evaluation-question-card
          [title]="answer.controls.questionTitle.value ?? ''"
          [hint]="answer.controls.questionHint.value"
          [required]="answer.controls.questionRequired.value"
          [control]="answer.controls.selections"
          [showValidationErrors]="submissionAttempted()"
          [canComment]="useProsemirror"
          [commentDiscovery]="commentDiscoveryActive()"
          [commentExpanded]="isCommentExpanded(answer)"
          (commentToggle)="toggleComment(answer)"
          [rtEvaluationFieldRef]="answer.controls.selections"
          margin-bottom-small
        >
          <rt-evaluation-multi-choice-field
            [selections]="answer.controls.selections"
            [options]="answer.controls.options.getRawValue()"
          />
          @if (isCommentExpanded(answer)) {
            <rt-evaluation-comment-field
              questionNote
              [control]="answer.controls.note"
              [jsonControl]="answer.controls.noteJson"
              [hasBox]="false"
              [scrollOnCreate]="wasCommentExpandedByUser(answer)"
              [rtFeatureTooltip]="commentTooltipConfig"
              [rtFeatureTooltipEnabled]="isCommentTooltipOpen(answer)"
              (rtFeatureTooltipDismissed)="onCommentTooltipDismissed()"
              (scrolledOnCreate)="onCommentScrolled(answer)"
              margin-top-small
            />
          }
        </rt-evaluation-question-card>
      }
      @case (QuestionKind.Dropdown) {
        <rt-evaluation-question-card
          [title]="answer.controls.questionTitle.value ?? ''"
          [hint]="answer.controls.questionHint.value"
          [required]="answer.controls.questionRequired.value"
          [control]="answer.controls.content"
          [showValidationErrors]="submissionAttempted()"
          [canComment]="useProsemirror"
          [commentDiscovery]="commentDiscoveryActive()"
          [commentExpanded]="isCommentExpanded(answer)"
          (commentToggle)="toggleComment(answer)"
          [rtEvaluationFieldRef]="answer.controls.content"
          margin-bottom-small
        >
          <rt-evaluation-dropdown-field
            [control]="answer.controls.content"
            [required]="answer.controls.questionRequired.value"
            [options]="answer.controls.options.getRawValue()"
          />
          @if (isCommentExpanded(answer)) {
            <rt-evaluation-comment-field
              questionNote
              [control]="answer.controls.note"
              [jsonControl]="answer.controls.noteJson"
              [hasBox]="false"
              [scrollOnCreate]="wasCommentExpandedByUser(answer)"
              [rtFeatureTooltip]="commentTooltipConfig"
              [rtFeatureTooltipEnabled]="isCommentTooltipOpen(answer)"
              (rtFeatureTooltipDismissed)="onCommentTooltipDismissed()"
              (scrolledOnCreate)="onCommentScrolled(answer)"
              margin-top-small
            />
          }
        </rt-evaluation-question-card>
      }
      @case (QuestionKind.Date) {
        <rt-evaluation-question-card
          [title]="answer.controls.questionTitle.value ?? ''"
          [hint]="answer.controls.questionHint.value"
          [required]="answer.controls.questionRequired.value"
          [control]="answer.controls.content"
          [showValidationErrors]="submissionAttempted()"
          [canComment]="useProsemirror"
          [commentDiscovery]="commentDiscoveryActive()"
          [commentExpanded]="isCommentExpanded(answer)"
          (commentToggle)="toggleComment(answer)"
          [rtEvaluationFieldRef]="answer.controls.content"
          margin-bottom-small
        >
          <rt-evaluation-date-field [control]="answer.controls.content" />
          @if (isCommentExpanded(answer)) {
            <rt-evaluation-comment-field
              questionNote
              [control]="answer.controls.note"
              [jsonControl]="answer.controls.noteJson"
              [hasBox]="false"
              [scrollOnCreate]="wasCommentExpandedByUser(answer)"
              [rtFeatureTooltip]="commentTooltipConfig"
              [rtFeatureTooltipEnabled]="isCommentTooltipOpen(answer)"
              (rtFeatureTooltipDismissed)="onCommentTooltipDismissed()"
              (scrolledOnCreate)="onCommentScrolled(answer)"
              margin-top-small
            />
          }
        </rt-evaluation-question-card>
      }
      @case (QuestionKind.Salary) {
        <rt-evaluation-question-card
          [title]="answer.controls.questionTitle.value ?? ''"
          [hint]="answer.controls.questionHint.value"
          [required]="answer.controls.questionRequired.value"
          [control]="answer.controls.content"
          [showValidationErrors]="submissionAttempted()"
          [canComment]="useProsemirror"
          [commentDiscovery]="commentDiscoveryActive()"
          [commentExpanded]="isCommentExpanded(answer)"
          (commentToggle)="toggleComment(answer)"
          [rtEvaluationFieldRef]="answer.controls.content"
          margin-bottom-small
        >
          <rt-evaluation-salary-field
            [control]="answer.controls.content"
            [currencyLabel]="currencyLabel(answer.controls.currency.value)"
          />
          @if (isCommentExpanded(answer)) {
            <rt-evaluation-comment-field
              questionNote
              [control]="answer.controls.note"
              [jsonControl]="answer.controls.noteJson"
              [hasBox]="false"
              [scrollOnCreate]="wasCommentExpandedByUser(answer)"
              [rtFeatureTooltip]="commentTooltipConfig"
              [rtFeatureTooltipEnabled]="isCommentTooltipOpen(answer)"
              (rtFeatureTooltipDismissed)="onCommentTooltipDismissed()"
              (scrolledOnCreate)="onCommentScrolled(answer)"
              margin-top-small
            />
          }
        </rt-evaluation-question-card>
      }
      @case (QuestionKind.Info) {
        <rt-evaluation-info-field
          [title]="answer.controls.questionTitle.value ?? ''"
          [description]="answer.controls.questionHint.value"
          margin-bottom-small
        />
      }
      @case (QuestionKind.Section) {
        <rt-evaluation-section-field
          [title]="answer.controls.questionTitle.value ?? ''"
          [description]="answer.controls.questionHint.value"
          margin-bottom-small
        />
      }
      @case (QuestionKind.File) {
        <rt-evaluation-question-card
          [title]="answer.controls.questionTitle.value ?? ''"
          [hint]="answer.controls.questionHint.value"
          [required]="answer.controls.questionRequired.value"
          [control]="answer.controls.attachmentIds"
          [showValidationErrors]="submissionAttempted()"
          [canComment]="useProsemirror"
          [commentDiscovery]="commentDiscoveryActive()"
          [commentExpanded]="isCommentExpanded(answer)"
          (commentToggle)="toggleComment(answer)"
          [rtEvaluationFieldRef]="answer.controls.attachmentIds"
          margin-bottom-small
        >
          <rt-evaluation-file-field
            [attachmentIds]="answer.controls.attachmentIds"
            [existingAttachments]="attachmentsByAnswerId().get(answer.controls.id.value) ?? []"
            [preview]="filePreview()"
          />
          @if (isCommentExpanded(answer)) {
            <rt-evaluation-comment-field
              questionNote
              [control]="answer.controls.note"
              [jsonControl]="answer.controls.noteJson"
              [hasBox]="false"
              [scrollOnCreate]="wasCommentExpandedByUser(answer)"
              [rtFeatureTooltip]="commentTooltipConfig"
              [rtFeatureTooltipEnabled]="isCommentTooltipOpen(answer)"
              (rtFeatureTooltipDismissed)="onCommentTooltipDismissed()"
              (scrolledOnCreate)="onCommentScrolled(answer)"
              margin-top-small
            />
          }
        </rt-evaluation-question-card>
      }
      @case (QuestionKind.Skills) {
        <rt-evaluation-skills-field
          [skillRatings]="answer.controls.skillRatings"
          [required]="answer.controls.questionRequired.value"
          [showValidationErrors]="submissionAttempted()"
          [rtEvaluationFieldRef]="answer.controls.skillRatings"
          margin-bottom-small
        />
      }
    }
  }

  <rt-evaluation-rating-advanced-field
    [ratingControl]="form.controls.score"
    [ratingScale]="ratingScale()"
    [commentControl]="form.controls.ratingNote"
    [commentJsonControl]="form.controls.ratingNoteJson"
    [showValidationErrors]="submissionAttempted()"
    [rtEvaluationFieldRef]="form.controls.score"
  />
</div>
